"use strict";
var __importDefault = (this && this.__importDefault) || function (mod) {
    return (mod && mod.__esModule) ? mod : { "default": mod };
};
Object.defineProperty(exports, "__esModule", { value: true });
exports.ModelInteractionsLayer = void 0;
const jsx_runtime_1 = require("react/jsx-runtime");
const react_1 = require("react");
const styled_components_1 = __importDefault(require("styled-components"));
const bridge_1 = require("@completeanatomy/bridge");
const device_1 = require("@completeanatomy/device");
const ui_1 = require("@completeanatomy/ui");
const Container = styled_components_1.default.div `
  width: 100vw;
  height: 100vh;

  touch-action: none;
`;
const ContainerWithInteractions = (0, ui_1.withMouseInteractions)((0, ui_1.withGestures)(Container), {
    clicksOnly: false,
    moveThreshold: 5,
    longPressThreshold: 1000,
});
const getActions = (bridge) => ({
    [ui_1.ActionType.NO_ACTION]: {},
    [ui_1.ActionType.SINGLE_CLICK]: {
        end: bridge === null || bridge === void 0 ? void 0 : bridge.singleClickAction,
    },
    [ui_1.ActionType.DOUBLE_CLICK]: {
        end: bridge === null || bridge === void 0 ? void 0 : bridge.doubleClickAction,
    },
    [ui_1.ActionType.LONG_CLICK]: {
        end: bridge === null || bridge === void 0 ? void 0 : bridge.longPressAction,
    },
    [ui_1.ActionType.ROTATE]: {
        start: bridge === null || bridge === void 0 ? void 0 : bridge.startRotateAction,
        perform: bridge === null || bridge === void 0 ? void 0 : bridge.rotateAction,
        end: bridge === null || bridge === void 0 ? void 0 : bridge.endRotateAction,
    },
    [ui_1.ActionType.DRAG]: {
        start: bridge === null || bridge === void 0 ? void 0 : bridge.startDragAction,
        perform: bridge === null || bridge === void 0 ? void 0 : bridge.dragAction,
        end: bridge === null || bridge === void 0 ? void 0 : bridge.endDragAction,
    },
    [ui_1.ActionType.SCALE]: {
        start: bridge === null || bridge === void 0 ? void 0 : bridge.startScaleAction,
        perform: bridge === null || bridge === void 0 ? void 0 : bridge.scaleAction,
        end: bridge === null || bridge === void 0 ? void 0 : bridge.endScaleAction,
    },
    [ui_1.ActionType.PINCH]: {
        start: bridge === null || bridge === void 0 ? void 0 : bridge.startScaleAction,
        perform: bridge === null || bridge === void 0 ? void 0 : bridge.scaleAction,
        end: bridge === null || bridge === void 0 ? void 0 : bridge.endScaleAction,
    },
    [ui_1.ActionType.TWIST]: {
        start: bridge === null || bridge === void 0 ? void 0 : bridge.startTwistAction,
        perform: bridge === null || bridge === void 0 ? void 0 : bridge.twistAction,
        end: bridge === null || bridge === void 0 ? void 0 : bridge.endTwistAction,
    },
    [ui_1.ActionType.MOUSE_DOWN]: {
        perform: bridge === null || bridge === void 0 ? void 0 : bridge.performMouseDownAction,
    },
    [ui_1.ActionType.MOUSE_UP]: {
        perform: bridge === null || bridge === void 0 ? void 0 : bridge.performMouseUpAction,
    },
});
// eslint-disable-next-line max-lines-per-function
function ModelInteractionsLayer(props) {
    const bridge = (0, bridge_1.useBridge)();
    const actions = getActions(bridge);
    const platform = (0, device_1.usePlatform)();
    const screenScale = (0, device_1.useOptimalScreenScale)();
    const shouldStartAction = (0, react_1.useCallback)((type, options) => {
        switch (type) {
            case ui_1.ActionType.TWIST:
                return !platform.isApple;
            case ui_1.ActionType.SCALE:
                /**
                 * Pinch gesture on MacOS trackpad is handled on the native side
                 */
                return !(platform.isDesktop && platform.isApple && options.metaKey);
            default:
                return true;
        }
    }, [platform.isApple, platform.isDesktop]);
    const { shouldPerformAction: shouldPerformActionProp } = props;
    const shouldPerformAction = (0, react_1.useCallback)((type, options) => {
        if (shouldPerformActionProp) {
            return shouldPerformActionProp(type, options);
        }
        switch (type) {
            case ui_1.ActionType.ROTATE:
            case ui_1.ActionType.DRAG:
            case ui_1.ActionType.TWIST:
                return !platform.isApple;
            case ui_1.ActionType.SCALE:
                /**
                 * Pinch gesture on MacOS trackpad is handled on the native side
                 */
                return !(platform.isDesktop && platform.isApple && options.metaKey);
            case ui_1.ActionType.PINCH:
                return !platform.isIOS;
            default:
                return true;
        }
    }, [
        platform.isApple,
        platform.isDesktop,
        platform.isIOS,
        shouldPerformActionProp,
    ]);
    const shouldEndAction = (0, react_1.useCallback)((type, options) => {
        switch (type) {
            case ui_1.ActionType.SCALE:
                /**
                 * Pinch gesture on MacOS trackpad is handled on the native side
                 */
                return !(platform.isDesktop && platform.isApple && options.metaKey);
            case ui_1.ActionType.TWIST:
                return !platform.isApple;
            default:
                return true;
        }
    }, [platform.isApple, platform.isDesktop]);
    const onContextMenu = (0, react_1.useCallback)((e) => {
        /**
         * Don't prevent context menu on mobile devices
         */
        if (!platform.isDesktop) {
            /**
             * Disable context menu for the container
             * It will help to avoid the context menu on the right click on the model
             */
            if (e.target === e.currentTarget) {
                e.preventDefault();
                e.stopPropagation();
            }
            return;
        }
        e.stopPropagation();
        if (e.metaKey && e.shiftKey) {
            return;
        }
        e.preventDefault();
    }, [platform.isDesktop]);
    const onKeyDownAction = (0, react_1.useCallback)((event) => {
        const data = {
            keyCode: event.which,
            shiftKey: event.shiftKey ? true : false,
            ctrlKey: event.ctrlKey ? true : false,
            metaKey: event.metaKey,
            altKey: event.altKey,
        };
        bridge === null || bridge === void 0 ? void 0 : bridge.keyDownAction(data);
    }, [bridge]);
    const onKeyUpAction = (0, react_1.useCallback)((event) => {
        const data = {
            keyCode: event.which,
            shiftKey: event.shiftKey ? true : false,
            ctrlKey: event.ctrlKey ? true : false,
            metaKey: event.metaKey,
            altKey: event.altKey,
        };
        bridge === null || bridge === void 0 ? void 0 : bridge.keyUpAction(data);
    }, [bridge]);
    const startAction = (0, react_1.useCallback)((type, options) => {
        var _a, _b;
        if ('start' in actions[type] && shouldStartAction(type, options)) {
            (_b = (_a = actions[type]) === null || _a === void 0 ? void 0 : _a.start) === null || _b === void 0 ? void 0 : _b.call(bridge, options);
        }
    }, [actions, bridge, shouldStartAction]);
    const performAction = (0, react_1.useCallback)((type, options) => {
        var _a, _b;
        if ('perform' in actions[type] && shouldPerformAction(type, options)) {
            (_b = (_a = actions[type]) === null || _a === void 0 ? void 0 : _a.perform) === null || _b === void 0 ? void 0 : _b.call(bridge, options);
        }
    }, [actions, bridge, shouldPerformAction]);
    const endAction = (0, react_1.useCallback)((type, options) => {
        var _a, _b;
        if ('end' in actions[type] && shouldEndAction(type, options)) {
            (_b = (_a = actions[type]) === null || _a === void 0 ? void 0 : _a.end) === null || _b === void 0 ? void 0 : _b.call(bridge, options);
        }
    }, [actions, bridge, shouldEndAction]);
    (0, react_1.useEffect)(() => {
        document.addEventListener(ui_1.contextmenu, onContextMenu);
        document.body.addEventListener('keydown', onKeyDownAction);
        document.body.addEventListener('keyup', onKeyUpAction);
        return () => {
            document.removeEventListener(ui_1.contextmenu, onContextMenu);
            document.body.removeEventListener('keydown', onKeyDownAction);
            document.body.removeEventListener('keyup', onKeyUpAction);
        };
    }, [onContextMenu, onKeyDownAction, onKeyUpAction, platform]);
    const getClickOptionsForAction = (0, react_1.useCallback)((type, data) => {
        switch (type) {
            case ui_1.ActionType.SINGLE_CLICK:
            case ui_1.ActionType.DOUBLE_CLICK:
            case ui_1.ActionType.LONG_CLICK:
                return {
                    point: (0, ui_1.multiplyPoint)(data.position, window.devicePixelRatio / (screenScale || window.devicePixelRatio)),
                    metaKey: data.metaKey,
                };
            case ui_1.ActionType.MOUSE_DOWN:
            case ui_1.ActionType.MOUSE_UP:
                return {
                    point: (0, ui_1.multiplyPoint)(data.position, window.devicePixelRatio / (screenScale || window.devicePixelRatio)),
                };
            default:
                return undefined;
        }
    }, [screenScale]);
    const getGestureOptionsForAction = (0, react_1.useCallback)((type, data) => {
        switch (type) {
            case ui_1.ActionType.ROTATE:
                return {
                    point: (0, ui_1.multiplyPoint)(data.position, window.devicePixelRatio / (screenScale || window.devicePixelRatio)),
                    delta: data.positionDelta,
                };
            case ui_1.ActionType.DRAG:
                return {
                    startPoint: (0, ui_1.multiplyPoint)(data.previousPosition, window.devicePixelRatio / (screenScale || window.devicePixelRatio)),
                    endPoint: (0, ui_1.multiplyPoint)(data.position, window.devicePixelRatio / (screenScale || window.devicePixelRatio)),
                };
            case ui_1.ActionType.SCALE:
            case ui_1.ActionType.PINCH:
                const SCALE_FACTOR = 5;
                return {
                    point: (0, ui_1.multiplyPoint)(data.position, window.devicePixelRatio / (screenScale || window.devicePixelRatio)),
                    scale: type === ui_1.ActionType.PINCH
                        ? SCALE_FACTOR * data.scaleDelta
                        : data.scale,
                    metaKey: data.metaKey,
                };
            case ui_1.ActionType.TWIST:
                return {
                    point: (0, ui_1.multiplyPoint)(data.position, window.devicePixelRatio / (screenScale || window.devicePixelRatio)),
                    angle: data.rotationDelta,
                };
            default:
                return undefined;
        }
    }, [screenScale]);
    const getOptionsForAction = (0, react_1.useCallback)((type, data) => {
        const clickOptions = getClickOptionsForAction(type, data);
        const gestureOptions = getGestureOptionsForAction(type, data);
        return clickOptions ? clickOptions : gestureOptions;
    }, [getClickOptionsForAction, getGestureOptionsForAction]);
    const onEvent = (0, react_1.useCallback)((type, phase, data) => {
        const options = getOptionsForAction(type, data);
        // return `false` from onEvent to prevent further actions
        const shouldInvokeNativeAction = !props.onEvent || props.onEvent(type, options);
        if (shouldInvokeNativeAction === false) {
            return;
        }
        switch (phase) {
            case ui_1.ActionPhase.Start:
                startAction(type, options);
                return;
            case ui_1.ActionPhase.Perform:
                performAction(type, options);
                return;
            case ui_1.ActionPhase.End:
                endAction(type, options);
                return;
        }
    }, [endAction, getOptionsForAction, performAction, props, startAction]);
    return (0, jsx_runtime_1.jsx)(ContainerWithInteractions, Object.assign({}, props, { onEvent: onEvent, "data-e2e": 'ModelInteractionsLayer_ContainerWithInteractions' }));
}
exports.ModelInteractionsLayer = ModelInteractionsLayer;
exports.default = ModelInteractionsLayer;
